
import React, { useState } from 'react';
import { Mail, Send, Loader2, Phone, Globe, AlertCircle, Linkedin, Twitter, Facebook } from 'lucide-react';
import { PHONE, EMAIL, WEBSITE, LINKEDIN_URL, X_URL, FACEBOOK_URL } from '../constants';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSuccess(true);
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setSuccess(false), 4000);
    }, 1200);
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
          <div className="w-full lg:w-1/3">
            <h2 className="text-[10px] font-bold text-blue-600 uppercase tracking-[0.3em] mb-3">Inquiry</h2>
            <h3 className="text-2xl md:text-3xl font-black text-slate-900 mb-6 tracking-tighter">Discuss Your Requirements</h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 mb-8">
              {[
                { icon: <Phone size={16} />, label: "Direct Line", val: PHONE },
                { icon: <Mail size={16} />, label: "Email", val: EMAIL },
                { icon: <Globe size={16} />, label: "Web", val: WEBSITE }
              ].map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="p-2.5 bg-slate-50 rounded-xl text-slate-900 border border-slate-100">
                    {item.icon}
                  </div>
                  <div>
                    <h4 className="text-[8px] uppercase font-bold text-slate-400 tracking-widest">{item.label}</h4>
                    <p className="font-bold text-slate-900 text-xs md:text-sm">{item.val}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex space-x-2 mb-8">
              <a href={LINKEDIN_URL} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-slate-50 rounded-xl border border-slate-100 hover:bg-avus-blue hover:text-white transition-all"><Linkedin size={16} /></a>
              <a href={X_URL} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-slate-50 rounded-xl border border-slate-100 hover:bg-slate-900 hover:text-white transition-all"><Twitter size={16} /></a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="p-2.5 bg-slate-50 rounded-xl border border-slate-100 hover:bg-[#1877F2] hover:text-white transition-all"><Facebook size={16} /></a>
            </div>

            <div className="p-5 bg-blue-50/50 rounded-2xl border border-blue-100">
              <div className="flex items-center space-x-2 text-blue-600 mb-2">
                <AlertCircle size={14} />
                <span className="text-[9px] font-bold uppercase tracking-widest">Regulatory Disclaimer</span>
              </div>
              <p className="text-[10px] text-slate-600 leading-relaxed font-medium">
                AVUS provides administrative, operational, and coordination services only. We do not provide legal advice.
              </p>
            </div>
          </div>

          <div className="w-full lg:w-2/3">
            <div className="bg-slate-50 rounded-[1.5rem] md:rounded-[2.5rem] p-6 md:p-10 border border-slate-100">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-slate-400 ml-1">Entity Name</label>
                    <input 
                      type="text" required value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-5 py-3.5 rounded-xl bg-white border border-slate-200 focus:outline-none focus:border-blue-600 transition-all font-bold text-slate-900 text-sm"
                      placeholder="Organization Name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[9px] font-bold uppercase tracking-widest text-slate-400 ml-1">Email</label>
                    <input 
                      type="email" required value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full px-5 py-3.5 rounded-xl bg-white border border-slate-200 focus:outline-none focus:border-blue-600 transition-all font-bold text-slate-900 text-sm"
                      placeholder="corporate@domain.com"
                    />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] font-bold uppercase tracking-widest text-slate-400 ml-1">Brief</label>
                  <textarea 
                    rows={4} required value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="w-full px-5 py-3.5 rounded-xl bg-white border border-slate-200 focus:outline-none focus:border-blue-600 transition-all font-bold text-slate-900 resize-none text-sm"
                    placeholder="Describe your needs..."
                  ></textarea>
                </div>
                <button 
                  type="submit" disabled={loading}
                  className="w-full bg-slate-900 text-white py-4 rounded-xl font-bold flex items-center justify-center hover:bg-blue-600 transition-all disabled:opacity-50 text-sm"
                >
                  {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit Inquiry"}
                </button>
                {success && (
                  <p className="text-green-600 text-center font-bold text-xs animate-bounce">
                    Inquiry Received. We will contact you shortly.
                  </p>
                )}
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
